import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { SidebarBrand, SwitchAppList } from "@/components/AppSidebar";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Home as HomeIcon, Bell, Mail, User, LogOut,
  Heart, MessageCircle, BookOpen, Megaphone,
  ChevronDown, LayoutGrid, Users, MoreHorizontal, Trash2,
  HelpCircle,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { format } from "date-fns";

const getInitials = (name) =>
  name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";

const formatDate = (iso) => {
  try { return format(new Date(iso), "MMMM yyyy"); } catch { return ""; }
};

const timeAgo = (iso) => {
  try {
    const s = Math.floor((Date.now() - new Date(iso)) / 1000);
    if (s < 60)   return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
};

const CATEGORY_STYLES = {
  prayer:   { color: "text-rose-600",   bg: "bg-rose-50",   border: "border-rose-200"   },
  praise:   { color: "text-amber-600",  bg: "bg-amber-50",  border: "border-amber-200"  },
  verse:    { color: "text-teal-600",   bg: "bg-teal-50",   border: "border-teal-200"   },
  sermon:   { color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-200" },
};
const CAT_ICONS = {
  prayer: Heart, praise: HelpCircle, verse: BookOpen, sermon: Megaphone,
};

const CATEGORY_LABELS = {
  prayer: "Prayer", praise: "Questions", verse: "Verse", sermon: "Sermon",
};

function CategoryPill({ category }) {
  const s = CATEGORY_STYLES[category];
  const Icon = CAT_ICONS[category];
  if (!s || !category) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${s.bg} ${s.color} ${s.border}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {CATEGORY_LABELS[category] ?? (category.charAt(0).toUpperCase() + category.slice(1))}
    </span>
  );
}

function MiniPostCard({ post, currentUser, onDelete, onReplyClick }) {
  const canDelete = currentUser?.role === "teacher" || post.author_id === currentUser?.user_id;
  return (
    <div
      data-testid={`profile-post-${post.post_id}`}
      onClick={() => onReplyClick(post)}
      className="px-5 py-4 border-b border-slate-100 hover:bg-slate-50/60 transition-colors cursor-pointer group"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          {post.category && <CategoryPill category={post.category} />}
          <span className="text-xs text-slate-400">{timeAgo(post.created_at)}</span>
        </div>
        {canDelete && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`profile-post-menu-${post.post_id}`}
                className="opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-slate-200 text-slate-400 shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem
                className="text-red-600 focus:text-red-700 focus:bg-red-50 gap-2"
                onClick={(e) => { e.stopPropagation(); onDelete(post.post_id); }}
              >
                <Trash2 className="w-4 h-4" />Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <p className="text-slate-700 text-sm leading-relaxed line-clamp-3">{post.content}</p>
      {post.image_url && (
        <img src={post.image_url} alt="" className="mt-2 rounded-lg max-h-40 w-full object-cover border border-slate-100" />
      )}
      <div className="flex items-center gap-5 mt-3" onClick={(e) => e.stopPropagation()}>
        <span className="flex items-center gap-1 text-xs text-slate-400">
          <MessageCircle className="w-3.5 h-3.5" />{post.reply_count || 0}
        </span>
        <span className={`flex items-center gap-1 text-xs ${post.liked_by_me ? "text-rose-500" : "text-slate-400"}`}>
          <Heart className={`w-3.5 h-3.5 ${post.liked_by_me ? "fill-current" : ""}`} />
          {post.like_count || 0}
        </span>
      </div>
    </div>
  );
}

// Sidebar shared across community pages
function CommunitySidebar({ user, navigate, activeItem = "profile" }) {
  const NavItem = ({ icon: Icon, label, id, onClick, badge = 0 }) => (
    <button
      data-testid={`community-nav-${id}`}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors w-full text-left group ${
        activeItem === id
          ? "bg-amber-50 text-amber-700 font-semibold"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`}
    >
      <div className="relative">
        <Icon className={`w-[18px] h-[18px] shrink-0 ${activeItem === id ? "text-amber-600" : "text-slate-400 group-hover:text-slate-600"}`} />
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center px-0.5">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
      <span className="text-sm hidden xl:block">{label}</span>
    </button>
  );

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } finally { navigate("/"); }
  };

  return (
    <aside className="hidden md:flex flex-col fixed top-0 left-0 h-screen w-[68px] xl:w-[260px] shrink-0 bg-white border-r border-slate-200 px-2 xl:px-4 py-4 overflow-y-auto z-40">
      <div className="h-16 shrink-0 flex items-center px-1 mb-1">
        <SidebarBrand brandLabel="Community" />
      </div>

      <SwitchAppList user={user} active="community" />

      <Separator className="mb-2" />

      <p className="px-2.5 mb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider hidden xl:block">
        Current App
      </p>

      <nav className="flex flex-col gap-0.5 flex-1">
        <NavItem icon={HomeIcon}   id="home"          label="Home"          onClick={() => navigate("/community")} />
        <NavItem icon={Bell}       id="notifications" label="Notifications" onClick={() => navigate("/community/notifications")} />
        <NavItem icon={Mail}       id="messages"      label="Messages"      onClick={() => navigate("/community/messages")} />
        <NavItem icon={User}       id="profile"       label="Profile"       onClick={() => navigate("/community/profile")} />
      </nav>

      <div className="my-2 border-t border-slate-100" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="community-user-menu"
            className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-100 transition-colors w-full">
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-amber-100 text-amber-700 text-xs font-semibold">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="hidden xl:block text-left min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="top" className="w-52">
          <DropdownMenuItem onClick={() => navigate("/community/profile")} className="gap-2">
            <User className="w-4 h-4" />My Profile
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleLogout} className="gap-2 text-red-600 focus:text-red-700 focus:bg-red-50">
            <LogOut className="w-4 h-4" />Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </aside>
  );
}

// ── Main page ──────────────────────────────────────────────
export default function CommunityProfile({ user }) {
  const navigate = useNavigate();
  const { userId } = useParams();
  const targetId = userId || "me";

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetchProfile();
  }, [targetId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/community/profile/${targetId}`);
      setProfile(data);
      setPosts(data.recent_posts || []);
    } catch {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/feed/${postId}`);
      setPosts((prev) => prev.filter((p) => p.post_id !== postId));
      // Update post_count in stats
      setProfile((prev) => prev ? {
        ...prev,
        stats: { ...prev.stats, post_count: prev.stats.post_count - 1 },
      } : prev);
      toast.success("Post deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleMessage = async () => {
    if (!profile) return;
    try {
      const { data } = await axios.post(`${API}/community/messages/conversations`, {
        recipient_id: profile.user_id,
      });
      navigate(`/community/messages?conv=${data.conversation_id}`);
    } catch {
      toast.error("Could not open conversation");
    }
  };

  const isOwnProfile = profile?.is_own_profile;
  const viewingUserId = profile?.user_id;
  const isOtherUser = !isOwnProfile && !!viewingUserId;

  const stats = [
    { label: "Posts",     value: profile?.stats?.post_count ?? 0 },
    { label: "Likes",     value: profile?.stats?.total_likes_received ?? 0 },
    { label: "Replies",   value: profile?.stats?.total_replies_received ?? 0 },
    { label: "Comments",  value: profile?.stats?.comments_written ?? 0 },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex md:ml-[68px] xl:ml-[260px] justify-center">

        {/* Left sidebar */}
        <CommunitySidebar
          user={user}
          navigate={navigate}
          activeItem={isOwnProfile ? "profile" : "home"}
        />

        {/* Main content */}
        <div className="flex w-full max-w-5xl">
        <main className="flex-1 min-w-0 border-x border-slate-200 bg-white">

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-slate-200 border-t-amber-500 rounded-full animate-spin" />
            </div>
          ) : !profile ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-8">
              <User className="w-12 h-12 text-slate-200 mb-4" />
              <h2 className="text-lg font-semibold text-slate-700">Profile not found</h2>
            </div>
          ) : (
            <>
              {/* Banner */}
              <div className="h-28 bg-gradient-to-r from-amber-100 via-amber-50 to-teal-50 shrink-0" />

              {/* Profile header */}
              <div className="px-6 pb-5 border-b border-slate-100">
                {/* Avatar + actions row */}
                <div className="flex items-end justify-between -mt-10 mb-4">
                  <Avatar className="h-20 w-20 ring-4 ring-white shrink-0">
                    <AvatarImage src={profile.picture} />
                    <AvatarFallback className="bg-amber-100 text-amber-700 text-2xl font-bold">
                      {getInitials(profile.name)}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex gap-2 mt-2">
                    {isOtherUser && (
                      <Button
                        data-testid="message-user-btn"
                        variant="outline"
                        size="sm"
                        onClick={handleMessage}
                        className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-50 gap-1.5 font-medium"
                      >
                        <Mail className="w-4 h-4" />
                        Message
                      </Button>
                    )}
                  </div>
                </div>

                {/* Name + role */}
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h1
                    data-testid="profile-name"
                    className="font-serif text-xl font-bold text-slate-900"
                  >
                    {profile.name}
                  </h1>
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs capitalize">
                    {profile.role}
                  </Badge>
                </div>

                {profile.member_since && (
                  <p className="text-xs text-slate-400 mb-4">
                    Member since {formatDate(profile.member_since)}
                  </p>
                )}

                {/* Stats row */}
                <div className="flex items-center gap-6 flex-wrap">
                  {stats.map(({ label, value }) => (
                    <div key={label} className="text-center" data-testid={`stat-${label.toLowerCase()}`}>
                      <p className="font-bold text-slate-900 text-lg leading-none">{value}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Posts section */}
              <div className="sticky top-0 bg-white/90 backdrop-blur-sm border-b border-slate-100 px-6 py-3 z-10">
                <h2 className="font-semibold text-slate-900 text-sm">
                  {isOwnProfile ? "Your posts" : `Posts by ${profile.name.split(" ")[0]}`}
                </h2>
              </div>

              {posts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
                  <MessageCircle className="w-10 h-10 text-slate-200 mb-3" />
                  <p className="text-slate-500 text-sm font-medium">
                    {isOwnProfile ? "You haven't posted yet." : "No posts yet."}
                  </p>
                  {isOwnProfile && (
                    <Button
                      data-testid="go-post-btn"
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate("/community")}
                      className="mt-3 text-amber-600 hover:text-amber-700"
                    >
                      Share something →
                    </Button>
                  )}
                </div>
              ) : (
                <div data-testid="profile-posts-list">
                  {posts.map((post) => (
                    <MiniPostCard
                      key={post.post_id}
                      post={post}
                      currentUser={user}
                      onDelete={handleDelete}
                      onReplyClick={() => navigate("/community", { state: { openPostId: post.post_id } })}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </main>
        </div>{/* max-w-5xl wrapper */}
      </div>

      {/* Mobile bottom bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex items-center justify-around px-4 py-2 z-50">
        <button onClick={() => navigate("/community")} className="p-2 text-slate-400"><HomeIcon className="w-6 h-6" /></button>
        <button onClick={() => navigate("/community/notifications")} className="p-2 text-slate-400"><Bell className="w-6 h-6" /></button>
        <button onClick={() => navigate("/community/messages")} className="p-2 text-slate-400"><Mail className="w-6 h-6" /></button>
        <button className="p-2 text-amber-600"><User className="w-6 h-6" /></button>
      </nav>
    </div>
  );
}
